import { useState, useEffect } from 'react';
import { Heart, MapPin, Calendar, ShoppingCart, TrendingDown, ExternalLink, Zap, Info, Loader2 } from 'lucide-react';
import { ProductListing, PriceTracking } from '../types';

interface ProductCardProps {
  product: ProductListing;
  isFavorite: boolean;
  onToggleFavorite: (product: ProductListing) => void;
  onAddToComparison: (product: ProductListing) => void;
  isTracked: boolean;
  onToggleTracking: (product: ProductListing) => void;
  trackedProduct?: PriceTracking;
  onViewDetails?: (product: ProductListing) => void;
  isImageLoading?: boolean;
}

export function ProductCard({
  product,
  isFavorite,
  onToggleFavorite,
  onAddToComparison,
  isTracked,
  onToggleTracking,
  trackedProduct,
  onViewDetails,
  isImageLoading = false,
}: ProductCardProps) {
  const images = product.images || [];
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const image = images[selectedImageIndex];
  const hasMultipleImages = images.length > 1;

  // Reset selected image when product changes
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [product.id]);

  const priceChange = trackedProduct
    ? trackedProduct.lowest_price && trackedProduct.current_price
      ? ((trackedProduct.current_price - trackedProduct.lowest_price) / trackedProduct.lowest_price) * 100
      : 0
    : null;

  const marketplaceColors = {
    FBM: 'bg-blue-500',
    OfferUp: 'bg-green-500',
    Amazon: 'bg-orange-500',
    Craigslist: 'bg-purple-500',
    eBay: 'bg-red-500',
  };

  const marketplaceLabels = {
    FBM: 'Facebook',
    OfferUp: 'OfferUp',
    Amazon: 'Amazon',
    Craigslist: 'Craigslist',
    eBay: 'eBay',
  };

  return (
    <div className="card card-hover overflow-hidden group">
      {/* Image Section */}
      <div className="relative overflow-hidden bg-neutral-100 dark:bg-neutral-800 aspect-square">
        {image ? (
          <>
            <img
              src={image}
              alt={product.title ? `${product.title} - Used Car Listing` : 'Used car for sale'}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              width="400"
              height="400"
            />
            {/* Loading overlay for Craigslist images */}
            {isImageLoading && (
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 size={24} className="animate-spin text-white" />
                  <span className="text-xs text-white font-medium">Loading image...</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-300 dark:text-neutral-600">
            <ShoppingCart size={40} className="sm:w-12 sm:h-12" />
          </div>
        )}

        {/* Overlay Actions */}
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex flex-col gap-1.5 sm:gap-2">
          <button
            onClick={() => onToggleFavorite(product)}
            className={`p-1.5 sm:p-2 rounded-lg transition-all duration-200 shadow-md backdrop-blur-sm ${
              isFavorite
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-white/90 dark:bg-neutral-800/90 text-neutral-600 dark:text-neutral-400 hover:bg-white dark:hover:bg-neutral-800 hover:text-red-500 dark:hover:text-red-400'
            }`}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart size={16} className="sm:w-[18px] sm:h-[18px]" fill={isFavorite ? 'currentColor' : 'none'} aria-hidden="true" />
          </button>

          {product.marketplace && (
            <div
              className={`px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-semibold text-white shadow-md ${marketplaceColors[product.marketplace]}`}
            >
              {marketplaceLabels[product.marketplace]}
            </div>
          )}
        </div>

        {/* Price Drop Badge */}
        {trackedProduct && priceChange !== null && priceChange < 0 && (
          <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 bg-green-500 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-semibold shadow-lg backdrop-blur-sm">
            <TrendingDown size={12} className="sm:w-3.5 sm:h-3.5" />
            {Math.abs(priceChange).toFixed(0)}% drop
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {hasMultipleImages && (
        <div className="px-4 sm:px-5 pt-2 pb-3 sm:pb-4 bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedImageIndex(idx)}
                className={`flex-shrink-0 w-12 sm:w-14 h-12 sm:h-14 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                  selectedImageIndex === idx
                    ? 'border-accent-500 dark:border-accent-400 ring-2 ring-accent-200 dark:ring-accent-800'
                    : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 opacity-70 hover:opacity-100'
                }`}
                aria-label={`View image ${idx + 1} of ${images.length}`}
              >
                <img
                  src={img}
                  alt={product.title ? `${product.title} - Used Car Image ${idx + 1}` : `Used car image ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="56"
                  height="56"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
        {/* Title */}
        <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 line-clamp-2 leading-snug group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors text-sm sm:text-base">
          <a 
            href={product.url} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hover:underline"
            aria-label={`View ${product.title} on ${product.marketplace || 'marketplace'}`}
          >
            {product.title}
          </a>
        </h3>

        {/* Price Section */}
        <div className="space-y-1.5 sm:space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
              {product.price ? `$${product.price.toLocaleString()}` : 'Price N/A'}
            </div>
            {trackedProduct && trackedProduct.lowest_price !== trackedProduct.current_price && (
              <div className="text-[10px] sm:text-xs text-neutral-500 dark:text-neutral-400 font-medium">
                Low: ${trackedProduct.lowest_price?.toLocaleString()}
              </div>
            )}
          </div>

          {product.condition && (
            <div className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 font-medium">Condition: {product.condition}</div>
          )}
        </div>

        {/* Metadata */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">
          {product.location && (
            <div className="flex items-center gap-1 sm:gap-1.5">
              <MapPin size={12} className="sm:w-3.5 sm:h-3.5" />
              <span className="truncate">{product.location}</span>
            </div>
          )}

          {product.date_posted && (
            <div className="flex items-center gap-1 sm:gap-1.5">
              <Calendar size={12} className="sm:w-3.5 sm:h-3.5" />
              <span>{new Date(product.date_posted).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Seller Info */}
        {product.seller_info?.rating && (
          <div className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
            <span className="font-medium">{product.seller_info.name}</span>
            {product.seller_info.rating && (
              <span className="ml-2 text-neutral-500 dark:text-neutral-500">
                ⭐ {product.seller_info.rating}
                {product.seller_info.review_count && ` (${product.seller_info.review_count})`}
              </span>
            )}
          </div>
        )}

        {/* Profit Analysis */}
        {product.profit_analysis && product.profit_analysis.is_underpriced && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 sm:p-3.5 space-y-1.5 sm:space-y-2">
            <div className="text-[10px] sm:text-xs font-bold text-green-800 dark:text-green-300 uppercase tracking-wider">💰 Resale Opportunity</div>
            {product.profit_analysis.potential_profit && (
              <div className="text-xs sm:text-sm text-green-700 dark:text-green-300">
                Potential Profit:{' '}
                <span className="font-bold">${product.profit_analysis.potential_profit.toFixed(2)}</span>
                {product.profit_analysis.profit_margin_percent && (
                  <span className="ml-2 text-[10px] sm:text-xs">
                    ({product.profit_analysis.profit_margin_percent.toFixed(1)}% margin)
                  </span>
                )}
              </div>
            )}
            {product.profit_analysis.estimated_market_value && (
              <div className="text-[10px] sm:text-xs text-green-600 dark:text-green-400">
                Est. Market Value: ${product.profit_analysis.estimated_market_value.toFixed(2)}
              </div>
            )}
            {product.profit_analysis.reasoning && (
              <div className="text-[10px] sm:text-xs text-neutral-600 dark:text-neutral-400 mt-1 italic">{product.profit_analysis.reasoning}</div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-2 pt-2">
          {onViewDetails && (
            <button
              onClick={() => onViewDetails(product)}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-400 hover:bg-accent-100 dark:hover:bg-accent-900/30 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 border border-accent-200 dark:border-accent-800 flex items-center justify-center gap-1.5 sm:gap-2"
              aria-label={`View details for ${product.title}`}
            >
              <Info size={12} className="sm:w-3.5 sm:h-3.5" aria-hidden="true" />
              View Details
            </button>
          )}
          
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onToggleTracking(product)}
              className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                isTracked
                  ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/40 border border-orange-200 dark:border-orange-800'
                  : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600 border border-neutral-200 dark:border-neutral-600'
              }`}
              aria-label={isTracked ? `Stop tracking price for ${product.title}` : `Track price for ${product.title}`}
            >
              {isTracked ? 'Tracking' : 'Track Price'}
            </button>

            <button
              onClick={() => onAddToComparison(product)}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 border border-neutral-200 dark:border-neutral-600 flex items-center justify-center gap-1 sm:gap-1.5"
              aria-label={`Add ${product.title} to comparison`}
            >
              <Zap size={12} className="sm:w-3.5 sm:h-3.5" aria-hidden="true" />
              Compare
            </button>
          </div>

          <a
            href={product.url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full btn-primary py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg flex items-center justify-center gap-1.5 sm:gap-2"
            aria-label={`View ${product.title} listing on ${product.marketplace || 'marketplace'}`}
          >
            <ExternalLink size={14} className="sm:w-4 sm:h-4" aria-hidden="true" />
            View Listing
          </a>
        </div>
      </div>
    </div>
  );
}
